import { useField } from "formik";
import { Card, Flex, Tag, Text } from "@jsq/ds/core";
import { FormSelect, FormSwitch } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { renderFormSelectOptions } from "../../../../shared/DataFormatSection/FormSelectOptions";
import {
  getFieldDataTypeTagContent,
  fieldDataTypeTag,
} from "../../../../shared/fieldDataTypeDisplay";
import { DECIMAL_PLACES_OPTIONS } from "../QuerySettingsPanel/constants";
import * as styles from "./styles_vanilla.css";
import type { FieldDataType, QueryFieldFieldsFragment } from "~/graphql/supergraphTypes";

type GeneralCardProps = {
  referenceKind: QueryFieldFieldsFragment["referenceKind"];
  isReadOnly?: boolean;
  queryDecimalPlaces?: number;
};

type FieldTypeOption = {
  value: FieldDataType;
  label: string;
};

const NUMERIC_FIELD_TYPE_OPTIONS: ReadonlyArray<FieldTypeOption> = [
  { value: "QUANTITY", label: "Quantity" },
  { value: "VALUE", label: "Value" },
  { value: "PERCENTAGE", label: "Percentage" },
];

const PRIMARY_FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  { value: "TEXT", label: "Text" },
  { value: "DATE", label: "Date" },
  { value: "QUANTITY", label: "Quantity" },
  { value: "VALUE", label: "Value" },
  { value: "PERCENTAGE", label: "Percentage" },
];

const DYNAMIC_DATE_FIELD_TYPE_OPTIONS: FieldTypeOption[] = [{ value: "DATE", label: "Date" }];

// NODE fields select the measure the field sums: VALUE (signed dollars) or
// QUANTITY (signed share counts). PERCENTAGE is not offered for nodes.
const NODE_FIELD_TYPE_OPTIONS: ReadonlyArray<FieldTypeOption> = [
  { value: "VALUE", label: "Value" },
  { value: "QUANTITY", label: "Quantity" },
];

const NUMERIC_FIELD_TYPES: ReadonlySet<FieldDataType> = new Set<FieldDataType>([
  "QUANTITY",
  "VALUE",
  "PERCENTAGE",
]);

const fieldTypeOptionsForKind = (
  referenceKind: QueryFieldFieldsFragment["referenceKind"],
): ReadonlyArray<FieldTypeOption> => {
  if (referenceKind === "PRIMARY_FIELD") return PRIMARY_FIELD_TYPE_OPTIONS;
  if (referenceKind === "DYNAMIC_DATE") return DYNAMIC_DATE_FIELD_TYPE_OPTIONS;
  if (referenceKind === "NODE") return NODE_FIELD_TYPE_OPTIONS;
  return NUMERIC_FIELD_TYPE_OPTIONS;
};

const FieldTypeSelect = ({
  options,
  isReadOnly,
}: {
  options: ReadonlyArray<FieldTypeOption>;
  isReadOnly: boolean;
}) => (
  <FormSelect
    name="fieldType"
    label="Field type"
    className={styles.fieldTypeSelect}
    data-testid="field-settings-field-type"
    disabled={isReadOnly}
  >
    <FormSelect.Section>
      {options.map((opt) => (
        <FormSelect.Option
          key={opt.value}
          value={opt.value}
          contentClassName={styles.fieldTypeOptionContent}
        >
          <span>{opt.label}</span>
          <Tag variant="neutral" className={fieldDataTypeTag}>
            {getFieldDataTypeTagContent(opt.value)}
          </Tag>
        </FormSelect.Option>
      ))}
    </FormSelect.Section>
  </FormSelect>
);

const DecimalPlacesRow = ({
  isReadOnly,
  queryDecimalPlaces,
}: {
  isReadOnly: boolean;
  queryDecimalPlaces?: number;
}) => {
  const [{ value: overrideDecimalPlaces }] = useField<boolean>("overrideDecimalPlaces");
  const switchLabel =
    queryDecimalPlaces !== undefined
      ? `Override query default (${queryDecimalPlaces})`
      : "Decimal places";

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.formControl}>
      <Text variant="strong-md">Decimal places</Text>
      <FormSwitch
        name="overrideDecimalPlaces"
        id="field-settings-override-decimal-places"
        label={switchLabel}
        size="small"
        disabled={isReadOnly}
        data-testid="field-settings-override-decimal-places"
      />
      {overrideDecimalPlaces && (
        <FormSelect
          name="format.decimalPlaces"
          label="Decimal places"
          hideLabel
          className={styles.formControl}
          data-testid="field-settings-decimal-places"
          disabled={isReadOnly}
        >
          {renderFormSelectOptions(DECIMAL_PLACES_OPTIONS)}
        </FormSelect>
      )}
    </Flex>
  );
};

export const GeneralCard = ({
  referenceKind,
  isReadOnly = false,
  queryDecimalPlaces,
}: GeneralCardProps) => {
  const [{ value: fieldType }] = useField<FieldDataType>("fieldType");
  const isNumeric = NUMERIC_FIELD_TYPES.has(fieldType);

  return (
    <Card isFullWidth title="General">
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        <FieldTypeSelect
          options={fieldTypeOptionsForKind(referenceKind)}
          isReadOnly={isReadOnly || referenceKind === "DYNAMIC_DATE"}
        />
        {isNumeric && (
          <DecimalPlacesRow isReadOnly={isReadOnly} queryDecimalPlaces={queryDecimalPlaces} />
        )}
      </Flex>
    </Card>
  );
};
